import React, { type ElementType, useState, useRef, useEffect } from 'react';

import { Box, Flex, IconProps, useOutsideClick } from '@chakra-ui/react';
import { Avatar } from '@material-ui/core';
import { useSelector } from 'react-redux';
import { DeleteAgendaModal } from 'screens/oneOnOneRabbit/InstancePage/common/modals/DeleteAgendaModal';
import useOneOnOnePageParams from 'screens/oneOnOneRabbit/hooks/useOneOnOnePageParams';
import { OneOnOneAgenda } from 'screens/oneOnOneRabbit/interfaces/OneOnOneAgenda';
import { selectLoggedInEmployeeId } from 'selectors/loggedInEmployee';
import { z } from 'zod';

import {
  ArrowUp,
  ChevronDown,
  Ellipsis,
  FeedbackIconDualTone,
  GoalIconDualTone,
  InitiativesIconDualTone,
  PlusNotOutlined,
  Trash,
} from 'assets/assetsV1/iconComponents';
import { Person } from 'assets/assetsV1/iconComponents/Person';

import { isMobileView } from 'constants/isMobileView';
import {
  sendAddedAgendaItemNotesEvent,
  sendClickedOnDeleteAgendaEvent,
  sendCompletedAgendaEvent,
} from 'constants/mixpanelHelper';

import { AutoResizeInput } from 'uiToolkitV1/AutoResizeInput';
// import { Avatar } from 'uiToolkitV1/Avatar';
import { Button } from 'uiToolkitV1/Button';
import { Checkbox } from 'uiToolkitV1/Checkbox';
import { DateComp } from 'uiToolkitV1/DateComp';
import { Divider } from 'uiToolkitV1/Divider';
import { Menu } from 'uiToolkitV1/Menu';
import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

import RichTextNote from '../../Notes/RichTextNote';
import {
  useAgendaCompletionMutation,
  useDeleteAgendaMutation,
  useUpdateAgendaMutation,
  useUpdateAgendaNotesMutation,
} from '../hooks/dataHooks';
import { AdminReorderHandle } from './AdminReorderHandle';

const DateText = ({ children }) => <TextLabel color="#666666">{children}</TextLabel>;

const REFERENCE_TYPE_ICON_TEXT_MAP = {
  GOAL: {
    icon: GoalIconDualTone,
    text: 'Goal',
  },
  INITIATIVE: {
    icon: InitiativesIconDualTone,
    text: 'Initiatives',
  },
  COMPETENCY: {
    icon: FeedbackIconDualTone,
    text: 'Competency',
  },
};

interface Props {
  isDragging?: boolean;
  isDraggingOver?: boolean;
  index: number;
}

type AgendaProps = z.infer<typeof OneOnOneAgenda>;

const AgendaItem = React.memo(({ isDragging, isDraggingOver, ...agenda }: AgendaProps & Props) => {
  const {
    agendaText,
    ownerDetails,
    isCompleted,
    sharedNotes,
    privateNotes,
    isOwnerAdmin,
    referenceType,
    instanceDataId,
    templateAgendaId,
    createdTs,
    index,
    isCarriedForward,
  } = agenda;

  const { masterId, instanceId } = useOneOnOnePageParams();

  const loggedInUserId: string = useSelector(selectLoggedInEmployeeId);

  const isAgendaAddedByCurrentUser = ownerDetails?.id === Number(loggedInUserId);
  const isAgendaEditable = !isOwnerAdmin && isAgendaAddedByCurrentUser;

  const inputRef = useRef<HTMLTextAreaElement>(null);

  const ReferenceIcon: ElementType<IconProps> = REFERENCE_TYPE_ICON_TEXT_MAP[referenceType]?.icon;
  const [isNotesView, setIsNotesView] = useState(false);

  const [agendaValue, setAgendaValue] = useState(agendaText);

  const [isAgendaError, setIsAgendaError] = useState(false);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    setAgendaValue(agendaText);
  }, [agendaText]);

  const updateAgendaMutation = useUpdateAgendaMutation({
    masterId,
    instanceId,
  });

  const updateAgendaCompletionMutation = useAgendaCompletionMutation({
    masterId,
    instanceId,
  });

  const updateAgendaNotesMutation = useUpdateAgendaNotesMutation({
    masterId,
    instanceId,
  });

  const deleteAgendaMutation = useDeleteAgendaMutation({
    masterId,
    instanceId,
  });

  const onAgendaNotesChange = async ({
    feedbackText,
    feedbackTextHtml,
    isPrivate,
  }: {
    feedbackText: string;
    feedbackTextHtml: string;
    isPrivate: boolean;
  }) => {
    if (feedbackText.trim() === agendaText.trim()) {
      return;
    }
    if (feedbackText.trim() !== '' && feedbackTextHtml.trim() !== '') {
      // Only mutate if either feedbackText or feedbackTextHtml has a value.
      updateAgendaNotesMutation.mutate({
        agendaNote: {
          instanceDataId,
          templateAgendaId,
          isPrivate,
          feedbackText,
          feedbackTextHtml,
        },
      });
    }

    sendAddedAgendaItemNotesEvent({
      eventProperties: {
        instance_id: instanceId,
        type: isPrivate ? 'Private' : 'Public',
      },
    });
  };

  const handleAgendaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const inputValue = e.target.value.slice(0, 255);
    setAgendaValue(inputValue);
  };

  const toggleNotesView = () => {
    setIsNotesView(!isNotesView);
  };

  const menuOptions = [
    {
      label: 'Delete Agenda',
      onClick: () => {
        sendClickedOnDeleteAgendaEvent({
          eventProperties: {
            instance_id: instanceId,
          },
        });
        setIsDeleteModalOpen(true);
      },
      Icon: Trash,
    },
  ];

  const updateAgendaHandler = async ({ agendaText }: { agendaText: string; isCompleted?: boolean }) => {
    if (agendaText.trim() === agenda.agendaText.trim()) {
      return;
    }
    if (!agendaText || agendaText.trim() === '') {
      setIsAgendaError(true);
      inputRef.current?.focus();
      return;
    }
    try {
      const updatedAgendaText = !agendaText || agendaText.trim() === '' ? agenda.agendaText : agendaText;
      const updatedAgenda = {
        ...agenda,
        agendaText: updatedAgendaText,
      };
      updateAgendaMutation.mutate({
        updatedAgenda,
      });
    } catch (e) {
      console.error('Error updating agenda : ', e);
    } finally {
      setIsAgendaError(false);
    }
  };

  const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      updateAgendaHandler({ agendaText: agendaValue });
      if (!agendaValue || agendaValue.trim() === '') {
        inputRef.current?.focus();
      } else {
        inputRef.current?.blur();
      }
    }
  };

  useOutsideClick({
    ref: inputRef,
    handler: () => updateAgendaHandler({ agendaText: agendaValue }),
  });

  const handleAgendaCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      updateAgendaCompletionMutation.mutate({
        instanceDataId,
        templateAgendaId,
        isCompleted: e.target.checked,
      });
      sendCompletedAgendaEvent({
        eventProperties: {
          instance_id: instanceId,
        },
      });
    } catch (error) {
      console.error('Error updating completion of agenda : ', e);
    }
  };
  return (
    <Flex
      sx={{
        pointerEvents: isDraggingOver ? 'none' : 'auto',
        ':hover': {
          backgroundColor: isDragging ? '#F6FAFE' : 'white',
        },
        backgroundColor: isDragging ? '#F6FAFE' : 'transparent',
        '&:hover .admin-reorder-handle': {
          visibility: 'visible',
        },
      }}
      p="16px 16px"
      paddingRight={isMobileView() ? '0px' : '16px'}
      alignItems="center"
      w="100%"
      pb="24px"
      // minHeight="115px"
      flexDir="column"
      position={isOwnerAdmin ? 'relative' : 'static'}
      mt={index === 0 && isDragging && !isOwnerAdmin && '24px'}
    >
      <Flex alignItems="flex-start" w="100%">
        <Checkbox
          mt="12px"
          isChecked={isCompleted}
          // @ts-expect-error type error
          onChange={handleAgendaCheckboxChange}
          data-testid="agenda-item-checkbox"
        />
        <Flex flexDir="column" w="100%">
          <Flex alignItems="center" w="100%">
            {isAgendaEditable ? (
              <AutoResizeInput
                ref={inputRef}
                isDashed
                value={agendaValue}
                onChange={handleAgendaChange}
                onKeyDown={onEnterPress}
                isInvalid={isAgendaError}
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  // fontWeight: '600'
                }}
                isReadOnly={isCompleted}
              />
            ) : (
              <TextBodyMedium
                sx={{
                  textDecoration: isCompleted ? 'line-through' : 'none',
                  // fontWeight: '600'
                }}
                w="100%"
              >
                {agendaText}
              </TextBodyMedium>
            )}

            {!isMobileView() && (
              <Button
                onClick={toggleNotesView}
                StartIcon={
                  sharedNotes?.feedbackText || privateNotes?.feedbackText
                    ? isNotesView
                      ? ArrowUp
                      : ChevronDown
                    : isNotesView
                      ? ArrowUp
                      : PlusNotOutlined
                }
                ml={isAgendaEditable ? '0px' : '90px'}
                variant="tertiary"
                data-testid="agenda-item-add-notes"
              >
                Notes
              </Button>
            )}
            {isOwnerAdmin ? (
              <Box height="40px" width={isMobileView() ? '0px' : '58px'} />
            ) : (
              <Flex data-testid="agenda-item-menu">
                <Menu
                  menuList={menuOptions}
                  MenuIcon={Ellipsis}
                  sx={isMobileView() ? { width: '24px', minWidth: '24px', height: '24px' } : {}}
                />
                <DeleteAgendaModal
                  isOpen={isDeleteModalOpen}
                  onClose={() => setIsDeleteModalOpen(false)}
                  onSave={() =>
                    deleteAgendaMutation.mutate({
                      instanceDataId,
                    })
                  }
                />
              </Flex>
            )}
          </Flex>
          <Flex flexDirection="column" w="100%">
            <Flex mt={!isOwnerAdmin ? '6px' : '0px'}>
              <TextLabel mr="4px" color="text.g2">
                Added by
              </TextLabel>
              {isOwnerAdmin ? (
                <Flex alignItems="center">
                  <Flex alignItems="center" justifyContent="center" mr="6px">
                    <Person boxSize="16px" color="#666666" />
                  </Flex>
                  <TextLabel color="text.g2">Admin</TextLabel>
                </Flex>
              ) : (
                <Flex alignItems="center">
                  <Avatar
                    style={{ marginRight: '4px', width: '12px', height: '12px' }}
                    src={ownerDetails?.profilePhoto}
                    alt={ownerDetails?.displayName}
                  />
                  <TextLabel color="text.g2">{ownerDetails?.displayName}</TextLabel>
                </Flex>
              )}
              {referenceType && referenceType !== 'CUSTOM' && (
                <TextLabel display="flex" ml="4px" color="text.g2">
                  • Related to <ReferenceIcon boxSize="16px" fill="transparent" />{' '}
                  {REFERENCE_TYPE_ICON_TEXT_MAP[referenceType].text}
                </TextLabel>
              )}
              {!isOwnerAdmin && !isCarriedForward && (
                <>
                  <TextLabel ml="4px" color="text.g2">
                    • &nbsp;
                  </TextLabel>
                  <DateComp
                    date={createdTs ? new Date(createdTs) : new Date()}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    textComponent={DateText}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                </>
              )}
              {isCarriedForward && (
                <>
                  <TextLabel ml="4px" color="text.g2">
                    • &nbsp;
                  </TextLabel>
                  <TextLabel color="text.g2">Carried over from previous 1:1</TextLabel>
                </>
              )}
            </Flex>
            {isMobileView() && (
              <Button
                onClick={toggleNotesView}
                StartIcon={
                  sharedNotes?.feedbackText || privateNotes?.feedbackText
                    ? isNotesView
                      ? ArrowUp
                      : ChevronDown
                    : isNotesView
                      ? ArrowUp
                      : PlusNotOutlined
                }
                ml="0px!important"
                paddingLeft="0px!important"
                width="fit-content"
                variant="tertiary"
                data-testid="agenda-item-add-notes"
              >
                Notes
              </Button>
            )}
          </Flex>
        </Flex>
      </Flex>
      {isNotesView && (
        <Box w="100%" data-testid="agenda-item-notes-container">
          <RichTextNote
            instanceDataId={instanceDataId}
            templateAgendaId={templateAgendaId}
            label="Shared Notes (visible to both)"
            onChange={onAgendaNotesChange}
            isPrivate={false}
            p="24px 8px 24px 24px"
            testId="agenda-item-shared-notes"
            {...sharedNotes}
          />
          <Divider opacity={1} color="#EBEFF3" />
          <RichTextNote
            instanceDataId={instanceDataId}
            templateAgendaId={templateAgendaId}
            label="Private Notes (visible only to you)"
            onChange={onAgendaNotesChange}
            isPrivate
            p="24px 8px 12px 24px"
            testId="agenda-item-private-notes"
            {...privateNotes}
          />
        </Box>
      )}
      {isOwnerAdmin && <AdminReorderHandle />}
    </Flex>
  );
});

export { AgendaItem };
